(() => {
  if (window.__pojSnapFooterReady) return;
  window.__pojSnapFooterReady = true;

  const html = document.documentElement;
  const finePointer = window.matchMedia('(pointer: fine)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fixedRevealViewport = window.matchMedia('(min-width: 768px)');
  let frameRequested = false;
  let footerReady = false;
  let pageLoaded = document.readyState === 'complete';

  function canAnimate() {
    return !reduceMotion.matches;
  }

  function syncHeaderHeight() {
    const bodyValue = getComputedStyle(document.body).getPropertyValue('--header-height').trim();
    html.style.setProperty('--arcade-header-height', bodyValue || '76px');
  }

  function syncFooterSurface() {
    const footer = document.querySelector('.arcade-footer');
    if (!footer) return;

    const footerStyles = getComputedStyle(footer);
    const footerBg = footerStyles.backgroundColor;
    const footerFg = footerStyles.color;
    const footerAccent = footerStyles.getPropertyValue('--footer-accent').trim();

    if (footerBg && footerBg !== 'rgba(0, 0, 0, 0)') {
      html.style.setProperty('--color-inverse-background', footerBg);
      document.querySelectorAll('.arcade-footer-corners').forEach((corners) => {
        corners.style.setProperty('--footer-bg', footerBg);
      });
    }

    if (footerFg) {
      html.style.setProperty('--color-inverse-foreground', footerFg);
      document.querySelectorAll('.arcade-footer-corners').forEach((corners) => {
        corners.style.setProperty('--footer-fg', footerFg);
      });
    }

    if (footerAccent) {
      html.style.setProperty('--color-focus', footerAccent);
    }
  }

  function markFooterReady() {
    if (footerReady || !pageLoaded) return;

    footerReady = true;
    delete html.dataset.arcadeFooterNavigating;
    html.dataset.arcadeFooterReady = 'true';
  }

  function shouldUseFixedReveal(footerHeight) {
    return (
      html.dataset.arcadeFooterReveal === 'true' &&
      fixedRevealViewport.matches &&
      !reduceMotion.matches &&
      footerHeight <= window.innerHeight * 0.94
    );
  }

  function updateFooterReveal() {
    const footerStage = document.querySelector('[data-arcade-footer-stage]');
    const main = document.querySelector('.arcade-page-main');
    if (!footerStage || !main) return;

    const footerHeight = footerStage.offsetHeight;
    const useFixedReveal = shouldUseFixedReveal(footerHeight);

    if (!useFixedReveal) {
      html.dataset.arcadeFooterStatic = 'true';
      html.style.setProperty('--arcade-footer-height', '0px');
      html.style.setProperty('--arcade-footer-y', '0%');
      markFooterReady();
      return;
    }

    html.removeAttribute('data-arcade-footer-static');
    html.style.setProperty('--arcade-footer-height', `${footerHeight}px`);

    const mainRect = main.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(html).getPropertyValue('--arcade-footer-reveal-gap')) || 0;
    const revealStart = window.innerHeight - gap;
    const progress = Math.max(0, Math.min(1, (revealStart - mainRect.bottom) / Math.max(footerHeight, 1)));
    const atPageEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    const y = atPageEnd || progress > 0.985 ? 0 : 18 - progress * 18;

    html.style.setProperty('--arcade-footer-y', `${y}%`);

    markFooterReady();
  }

  function hideFooterForNavigation() {
    footerReady = false;
    delete html.dataset.arcadeFooterReady;
    html.dataset.arcadeFooterNavigating = 'true';
    html.style.setProperty('--arcade-footer-y', '100%');
  }

  function shouldHideForLink(link) {
    if (!link || link.target || link.hasAttribute('download')) return false;
    if (link.getAttribute('href')?.startsWith('#')) return false;

    try {
      const url = new URL(link.href, window.location.href);
      return url.origin === window.location.origin && url.href !== window.location.href;
    } catch (error) {
      return false;
    }
  }

  function bindNavigationGuard() {
    if (html.dataset.arcadeFooterNavGuard === 'true') return;
    html.dataset.arcadeFooterNavGuard = 'true';

    document.addEventListener(
      'click',
      (event) => {
        const link = event.target?.closest?.('a[href]');
        if (shouldHideForLink(link)) hideFooterForNavigation();
      },
      true
    );

    window.addEventListener('pagehide', hideFooterForNavigation);
    window.addEventListener('beforeunload', hideFooterForNavigation);
    window.addEventListener('pageshow', () => {
      pageLoaded = true;
      delete html.dataset.arcadeFooterNavigating;
      requestFrame();
    });
  }

  function updateButtonPointer(button, event) {
    const rect = button.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / Math.max(rect.width, 1)) * 100;
    const y = ((event.clientY - rect.top) / Math.max(rect.height, 1)) * 100;
    button.style.setProperty('--arcade-button-fill-x', `${Math.max(0, Math.min(100, x))}%`);
    button.style.setProperty('--arcade-button-fill-y', `${Math.max(0, Math.min(100, y))}%`);

    if (html.dataset.arcadeButtonEffect !== 'magnetic_fill' || !canAnimate()) return;
    button.style.setProperty('--arcade-button-magnet-x', `${(x - 50) * 0.08}px`);
    button.style.setProperty('--arcade-button-magnet-y', `${(y - 50) * 0.08}px`);
  }

  function releaseButton(button, event) {
    updateButtonPointer(button, event);
    button.style.setProperty('--arcade-button-magnet-x', '0px');
    button.style.setProperty('--arcade-button-magnet-y', '0px');
  }

  function initButtons(scope = document) {
    if (!finePointer.matches) return;

    const buttons = [];
    if (scope.matches?.('.arcade-footer-stage .arcade-button')) buttons.push(scope);
    scope.querySelectorAll?.('.arcade-footer-stage .arcade-button').forEach((button) => buttons.push(button));

    buttons.forEach((button) => {
      if (button.dataset.arcadeButtonReady === 'true') return;
      button.dataset.arcadeButtonReady = 'true';
      button.addEventListener('pointerenter', (event) => updateButtonPointer(button, event));
      button.addEventListener('pointermove', (event) => updateButtonPointer(button, event));
      button.addEventListener('pointerleave', (event) => releaseButton(button, event));
    });
  }

  function frame() {
    syncHeaderHeight();
    syncFooterSurface();
    updateFooterReveal();
    frameRequested = false;
  }

  function requestFrame() {
    if (frameRequested) return;
    frameRequested = true;
    window.requestAnimationFrame(frame);
  }

  function init(scope = document) {
    bindNavigationGuard();
    initButtons(scope);
    requestFrame();
  }

  init();
  document.addEventListener('DOMContentLoaded', () => init());
  window.addEventListener('load', () => {
    pageLoaded = true;
    requestFrame();
  });
  window.addEventListener('scroll', requestFrame, { passive: true });
  window.addEventListener('resize', requestFrame);
  fixedRevealViewport.addEventListener?.('change', requestFrame);
  fixedRevealViewport.addListener?.(requestFrame);
  reduceMotion.addEventListener?.('change', requestFrame);
  reduceMotion.addListener?.(requestFrame);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', requestFrame);
  document.addEventListener('arcade:content:load', (event) => init(event.detail?.scope || document));
  setTimeout(requestFrame, 500);
})();
